import { useCallback, useContext, useId, useState } from 'react';
import ReactSelect, { type GroupBase, type MenuPosition } from 'react-select';
import CreatableSelect from 'react-select/creatable';
import AsyncCreatableSelect from 'react-select/async-creatable';
import { components } from './reactSelectComponents';
import {
  GenericAsyncCreatableSelectProps,
  GenericAsyncSelectProps,
  GenericCreatableSelectProps,
  GenericProps,
  GenericSelectProps,
  SelectOption,
  SelectOptionsOrGroups,
} from '../types';
import AsyncSelect from 'react-select/async';
import { zIndex } from '@ardoq/design-tokens';
import { useModalPortalTarget } from '@ardoq/hooks';
import { GenericSelectMenuPortalRootContext } from '@ardoq/stack-page-manager';
import { DEFAULT_ARDOQ_I18NLOCALE, type I18nLocale, t } from '@ardoq/i18n';
import { SelectMenuPortalTargetContext } from './SelectMenuPortalContext';

const getAsyncNoOptionsMessage =
  (i18nLocale: I18nLocale) =>
  ({ inputValue }: { inputValue: string }) =>
    inputValue === ''
      ? t(i18nLocale, 'select:typeToSearch', 'Type to search')
      : t(i18nLocale, 'select:noOptions', 'No options');

const customStyles = {
  // make z-index at least equal to <StackPageManager />
  // so select in portal renders well in modals
  menuPortal: (base: object) => ({ ...base, zIndex: zIndex.DROPDOWN }),
};

type SelectComponentOverrides<TValue> = NonNullable<
  GenericProps<TValue>['components']
>;

type MergedSelectComponents<TValue> = {
  [K in keyof typeof components]:
    | (typeof components)[K]
    | Exclude<SelectComponentOverrides<TValue>[K], undefined>;
} & Omit<SelectComponentOverrides<TValue>, keyof typeof components>;

type DefaultSelectProps<TValue> = {
  styles: typeof customStyles;
  menuPortalTarget: HTMLElement | null;
  captureMenuScroll: boolean;
  components: MergedSelectComponents<TValue>;
};

type MenuPortalProps = {
  menuPortalTarget: HTMLElement | null;
  menuPosition: MenuPosition | undefined;
};

/**
 * Resolves where the menu portals to, honouring an explicit `menuPortalTarget`
 * (including `null`, which renders the menu inline).
 *
 * Inside an `AnimatedModalDialog` the menu goes into the modal's own portal
 * container rather than `document.body`. The modal renders in the browser's top
 * layer, and nothing outside the top layer can paint over it at any `z-index` —
 * a body-level menu would simply be hidden behind the dialog.
 *
 * The modal container is also the one target that gets `menuPosition: 'fixed'`.
 * react-select's default `absolute` adds `window.pageYOffset` to the menu
 * offset, which is right only where the portal wrapper positions against the
 * document; the modal's container sits inside a `position: fixed` element, so
 * an absolute menu is displaced by the scroll offset. No other target is
 * remapped, the stack-page root included, and an explicit `menuPosition`
 * always wins.
 */
export const useMenuPortal = (
  menuPortalTarget: HTMLElement | null | undefined,
  menuPosition: MenuPosition | undefined
): MenuPortalProps => {
  const modalPortalTarget = useModalPortalTarget();
  const stackPageTarget = useContext(GenericSelectMenuPortalRootContext);
  const targetOverride = useContext(SelectMenuPortalTargetContext);

  const target =
    menuPortalTarget !== undefined
      ? menuPortalTarget
      : targetOverride !== undefined
        ? targetOverride
        : (modalPortalTarget ?? stackPageTarget ?? document.body);

  const isInsideModal = target !== null && target === modalPortalTarget;

  return {
    menuPortalTarget: target,
    menuPosition: isInsideModal ? (menuPosition ?? 'fixed') : menuPosition,
  };
};

export const getDefaultSelectProps = <TValue,>({
  menuPortalTarget,
  components: propsComponents = {},
}: Pick<
  GenericProps<TValue>,
  'menuPortalTarget' | 'components'
>): DefaultSelectProps<TValue> => ({
  styles: customStyles,
  menuPortalTarget:
    menuPortalTarget !== undefined ? menuPortalTarget : document.body,
  captureMenuScroll: false,
  components: { ...components, ...propsComponents },
});

export const GenericSelect = <TValue,>({
  menuPlacement = 'auto',
  components,
  ref,
  id,
  ...props
}: GenericSelectProps<TValue, false>) => {
  const generatedTextInputId = useId();
  const inputId = id ?? generatedTextInputId;
  const { menuPortalTarget, menuPosition } = useMenuPortal(
    props.menuPortalTarget,
    props.menuPosition
  );

  return (
    // @ts-expect-error I don't know how to fix it yet.
    <ReactSelect
      ref={ref}
      inputId={inputId}
      isMulti={false}
      menuPlacement={menuPlacement}
      aria-label={props.label}
      required={
        props.isRequired || props.required || props.indicator === 'required'
      }
      {...getDefaultSelectProps({ ...props, components })}
      {...props}
      menuPortalTarget={menuPortalTarget}
      menuPosition={menuPosition}
    />
  );
};

export const GenericCreatableSelect = <TValue, TIsMulti extends boolean>({
  components,
  ...props
}: GenericCreatableSelectProps<TValue, TIsMulti>) => {
  const generatedTextInputId = useId();
  const id = props.id ?? generatedTextInputId;
  const { menuPortalTarget, menuPosition } = useMenuPortal(
    props.menuPortalTarget,
    props.menuPosition
  );

  return (
    // @ts-expect-error I don't know how to fix it yet.
    <CreatableSelect
      inputId={id}
      {...getDefaultSelectProps({ ...props, components })}
      {...props}
      menuPortalTarget={menuPortalTarget}
      menuPosition={menuPosition}
    />
  );
};

export const GenericAsyncSelect = <TValue, TIsMulti extends boolean>({
  components,
  selectRef,
  menuPlacement,
  loadOptions,
  enableMenuPlacementFix,
  ...props
}: GenericAsyncSelectProps<TValue, TIsMulti>) => {
  const generatedTextInputId = useId();
  const id = props.id ?? generatedTextInputId;
  const { menuPortalTarget, menuPosition } = useMenuPortal(
    props.menuPortalTarget,
    props.menuPosition
  );

  // Due to a bug in React Select where menu placement doesn't get updated after options
  // are loaded, we need to manually set it to 'top' when there's not enough space
  // See https://github.com/JedWatson/react-select/issues/5642
  // Note: the fix can break some existing suggestion functions, enable it carefully
  const [patchedMenuPlacement, setPatchedMenuPlacement] =
    useState(menuPlacement);
  const i18nLocale = props.i18nLocale ?? DEFAULT_ARDOQ_I18NLOCALE;

  const patchedLoadOptions = useCallback(
    async (
      input: string,
      callback: (options: SelectOptionsOrGroups<TValue>) => void
    ) => {
      const options = await loadOptions?.(input, callback);

      const menuListRef = selectRef?.current?.menuListRef;
      if (menuListRef && options) {
        const { bottom } = menuListRef.getBoundingClientRect();
        if (bottom && bottom + options.length * 40 > window.innerHeight) {
          setPatchedMenuPlacement('top');
        }
      }
      return options;
    },
    [loadOptions, selectRef]
  );

  return (
    // @ts-expect-error I don't know how to fix it yet.
    <AsyncSelect<
      SelectOption<TValue>,
      TIsMulti,
      GroupBase<SelectOption<TValue>>
    >
      inputId={id}
      noOptionsMessage={getAsyncNoOptionsMessage(i18nLocale)}
      {...getDefaultSelectProps({ ...props, components })}
      {...props}
      loadOptions={enableMenuPlacementFix ? patchedLoadOptions : loadOptions}
      menuPlacement={patchedMenuPlacement}
      onMenuClose={() => setPatchedMenuPlacement(menuPlacement)}
      ref={selectRef}
      menuPortalTarget={menuPortalTarget}
      menuPosition={menuPosition}
    />
  );
};

export const GenericAsyncCreatableSelect = <TValue, TIsMulti extends boolean>({
  components,
  selectRef,
  menuPlacement,
  loadOptions,
  enableMenuPlacementFix,
  ...props
}: GenericAsyncCreatableSelectProps<TValue, TIsMulti>) => {
  const generatedTextInputId = useId();
  const id = props.id ?? generatedTextInputId;
  const { menuPortalTarget, menuPosition } = useMenuPortal(
    props.menuPortalTarget,
    props.menuPosition
  );

  // Due to a bug in React Select where menu placement doesn't get updated after options
  // are loaded, we need to manually set it to 'top' when there's not enough space
  // See https://github.com/JedWatson/react-select/issues/5642
  // Note: the fix can break some existing suggestion functions, enable it carefully
  const [patchedMenuPlacement, setPatchedMenuPlacement] =
    useState(menuPlacement);
  const i18nLocale = props.i18nLocale ?? DEFAULT_ARDOQ_I18NLOCALE;

  const patchedLoadOptions = useCallback(
    async (
      input: string,
      callback: (options: SelectOptionsOrGroups<TValue>) => void
    ) => {
      const options = await loadOptions?.(input, callback);

      const menuListRef = selectRef?.current?.menuListRef;
      if (menuListRef && options) {
        const { bottom } = menuListRef.getBoundingClientRect();
        if (bottom && bottom + options.length * 40 > window.innerHeight) {
          setPatchedMenuPlacement('top');
        }
      }
      return options;
    },
    [loadOptions, selectRef]
  );

  return (
    // @ts-expect-error I don't know how to fix it yet.
    <AsyncCreatableSelect<
      SelectOption<TValue>,
      TIsMulti,
      GroupBase<SelectOption<TValue>>
    >
      inputId={id}
      noOptionsMessage={getAsyncNoOptionsMessage(i18nLocale)}
      {...getDefaultSelectProps({ ...props, components })}
      {...props}
      loadOptions={enableMenuPlacementFix ? patchedLoadOptions : loadOptions}
      menuPlacement={patchedMenuPlacement}
      onMenuClose={() => setPatchedMenuPlacement(menuPlacement)}
      ref={selectRef}
      menuPortalTarget={menuPortalTarget}
      menuPosition={menuPosition}
    />
  );
};
